// shared/src/oracle/lineage.ts
// #98 (end-to-end per-metric LINEAGE) + #76 (consumer-provenance) — a PURE, READ-ONLY projection.
// Design: docs/oracle-lineage-scout-2026-07-21.md. NO new writes — every lineage stage assembles
// from data D1 already holds:
//   metric        → oracle_canonical.key
//   sources       → provenance_json.used[]/rejected[] ∪ oracle_sources config ∪ oracle_source_health
//   policy        → oracle_policy (mode + knobs) + the gateway kind descriptor (resolution/clock)
//   resolution    → provenance_json (median/mad/threshold/eff_weight_sum) + policy_version
//   value         → oracle_canonical (value/observed_at/status)
//   consumers(#76)→ CONSUMER_MAP below — a STATIC code fact (there is NO per-read D1 log)
//   usage         → DEFERRED: Analytics Engine is a NEW read capability (price-only, per-KEY not
//                   per-consumer) — omitted in v1; the UI shows "usage telemetry not wired", never a
//                   fabricated 0.
//
// The api-worker builder reads the D1 rows and hands them here; this file does the shaping so it is
// unit-testable in shared/ with no Worker/D1 dependency (mirrors source-registry.ts / dash-bundle.ts).

import { z } from 'zod';
import type { OracleSourceRow, OracleCanonicalRow, OracleSourceHealthRow, OraclePolicyRow } from './oracle-store.js';
import type { OracleMacroSeriesRow } from './macro-series-store.js';
import type { AnyKindDescriptor } from '../dataplane/registry.js';

// ─────────────────────── #76 consumer map (STATIC code fact) ───────────────────────
// "Where each metric is USED." Consumer identity is NOT in any D1 table — it is the set of gateway
// reader CALL SITES, a fixed code fact. Keyed by (file, reader): the exact LINE is deliberately NOT
// stored — a line number rots on the next edit and manufactures false drift (feedback_no_cross_file_
// claims_in_comments); file + reader + purpose is the stable, human-meaningful answer #76 asks for.
// COMPLETENESS is guarded by lineage.consumers.test.ts, which greps the readers across workers/ and
// asserts every call site's file is represented here — so a NEW call site fails a test, not silently
// drifts. Source-of-truth grep: `readPricePoints|readPriceSeries|readMacroBlock|readNewsWindow` in
// workers/**/src (call sites only). Verified 2026-07-21 (docs/oracle-lineage-scout-2026-07-21.md §1).
export type GatewayReader = 'readPricePoints' | 'readPriceSeries' | 'readMacroBlock' | 'readNewsWindow';
export type ReaderClock = 'latest' | 'run' | 'asOf';

export interface ConsumerSite {
  readonly reader:    GatewayReader;
  readonly data_type: 'price' | 'macro' | 'news';
  readonly file:      string;    // worker-relative path (stable); no line (rots)
  readonly clock:     ReaderClock;
  readonly purpose:   string;
}

export const CONSUMER_MAP: readonly ConsumerSite[] = [
  // PRICE — readPricePoints (point)
  { reader: 'readPricePoints', data_type: 'price', file: 'chain-workflow/src/stages/p-stage.ts',                 clock: 'run',    purpose: "price the brief's target tickers (P-stage)" },
  { reader: 'readPricePoints', data_type: 'price', file: 'chain-workflow/src/stages/i.1.assert-targets-priced.ts', clock: 'run',  purpose: 'gate: every target must be priced before proceeding' },
  { reader: 'readPricePoints', data_type: 'price', file: 'chain-workflow/src/stages/f.1.finalize.ts',            clock: 'run',    purpose: 'pin the honest oracle signal_price to T_run' },
  { reader: 'readPricePoints', data_type: 'price', file: 'refresh-worker/src/lib/score-signals.ts',              clock: 'latest', purpose: 'live signal scoring (real-time)' },
  { reader: 'readPricePoints', data_type: 'price', file: 'api-worker/src/routes/signal.ts',                      clock: 'latest', purpose: "serve one ticker's current price to the drill panel" },
  { reader: 'readPricePoints', data_type: 'price', file: 'api-worker/src/lib/world-state-projection.ts',         clock: 'latest', purpose: 'WORLD-STATE indicator prices' },
  // PRICE — readPriceSeries (series)
  { reader: 'readPriceSeries', data_type: 'price', file: 'api-worker/src/lib/tracker-bundle.ts',                 clock: 'latest', purpose: 'per-ticker consensus price history' },
  { reader: 'readPriceSeries', data_type: 'price', file: 'api-worker/src/lib/sparkline-pivot.ts',                clock: 'latest', purpose: 'dashboard sparklines' },
  // MACRO — readMacroBlock (assembled block)
  { reader: 'readMacroBlock',  data_type: 'macro', file: 'chain-workflow/src/stages/i.1.fetch.ts',              clock: 'run',    purpose: 'reassemble computed.macro for the brief as-of T_run' },
  { reader: 'readMacroBlock',  data_type: 'macro', file: 'api-worker/src/lib/world-state-projection.ts',         clock: 'latest', purpose: 'WORLD-STATE macro block' },
  // NEWS — readNewsWindow (bitemporal collection) — applies to news metrics (not price/macro lineage)
  { reader: 'readNewsWindow',  data_type: 'news',  file: 'chain-workflow/src/stages/i.1.fetch.ts',              clock: 'run',    purpose: 'RSS news digest into the brief' },
  { reader: 'readNewsWindow',  data_type: 'news',  file: 'chain-workflow/src/stages/i.1.fetch.ts',              clock: 'run',    purpose: 'per-ticker/search news window' },
  { reader: 'readNewsWindow',  data_type: 'news',  file: 'p-stage-worker/src/lib/news.ts',                     clock: 'run',    purpose: 'per-ticker oracle news read for the p-stage analysts (#125)' },
];

// ─────────────────────── view schemas ───────────────────────
export const LineageSourceViewSchema = z.object({
  source_id:   z.string(),
  role:        z.enum(['used', 'rejected']),
  value:       z.number().nullable(),
  weight:      z.number().nullable(),                                  // effective weight (used[] only)
  f_freshness: z.number().nullable(),                                  // from used[]/oracle_source_health
  verdict:     z.enum(['ok', 'quiet', 'frozen']).nullable(),          // oracle_source_health (consensus keys)
  reason:      z.enum(['error', 'no_value', 'stale', 'unconfigured', 'outlier', 'disagree']).nullable(),
  tier:        z.enum(['A', 'B', 'C']).nullable(),                    // oracle_sources config
  enabled:     z.boolean().nullable(),
});

export const LineageConsumerViewSchema = z.object({
  reader:  z.enum(['readPricePoints', 'readPriceSeries', 'readMacroBlock', 'readNewsWindow']),
  file:    z.string(),
  clock:   z.enum(['latest', 'run', 'asOf']),
  purpose: z.string(),
});

export const LineageMetricViewSchema = z.object({
  key:            z.string(),
  data_type:      z.enum(['price', 'macro']),
  value:          z.number().nullable(),
  // macro fields can be text (e.g. cmc_crypto_mkt_cap='2.35T', fear_greed_label='Extreme Fear');
  // price metrics are always numeric so value_text is null there. The UI shows value_text when the
  // numeric value is null, so a text macro field renders its real value rather than a blank '—'.
  value_text:     z.string().nullable(),
  status:         z.enum(['ok', 'below_quorum', 'no_candidates']),
  observed_at:    z.string().nullable(),
  created_at:     z.string(),
  policy_version: z.string(),
  policy: z.object({
    mode:        z.enum(['consensus', 'authoritative']),
    asset_class: z.string(),
    resolution:  z.string().nullable(),   // from the gateway kind descriptor (consensus|assembled|…)
    truth_clock: z.string().nullable(),
  }),
  sources:        z.array(LineageSourceViewSchema),
  // authoritative-macro provenance (#79): corroborator count behind the issuer. null for price.
  n_corroborators: z.number().nullable(),
  eff_weight_sum: z.number().nullable(),
  median:         z.number().nullable(),
  spread:         z.number().nullable(),
  mad:            z.number().nullable(),
  threshold:      z.number().nullable(),
  consumers:      z.array(LineageConsumerViewSchema),
  usage:          z.null(),   // v1: AE reader deferred — always null, never a fabricated count
});

export const LineageBundleSchema = z.object({
  generated_at: z.string(),
  metrics:      z.array(LineageMetricViewSchema),
});

export type LineageSourceView   = z.infer<typeof LineageSourceViewSchema>;
export type LineageConsumerView = z.infer<typeof LineageConsumerViewSchema>;
export type LineageMetricView   = z.infer<typeof LineageMetricViewSchema>;
export type LineageBundle       = z.infer<typeof LineageBundleSchema>;

// ─────────────────────── provenance parse (parse-don't-cast the D1 blob) ───────────────────────
const UsedSchema = z.object({
  source_id:   z.string(),
  value:       z.number(),
  weight:      z.number(),
  age_s:       z.number().optional(),
  f_freshness: z.number().optional(),
});
const RejectedSchema = z.object({
  source_id: z.string(),
  value:     z.number().nullable(),
  reason:    z.enum(['error', 'no_value', 'stale', 'unconfigured', 'outlier', 'disagree']),
  delta:     z.number().optional(),
});
const ProvenanceSchema = z.object({
  used:           z.array(UsedSchema).default([]),
  rejected:       z.array(RejectedSchema).default([]),
  median:         z.number().nullable().default(null),
  spread:         z.number().nullable().default(null),
  mad:            z.number().nullable().default(null),
  threshold:      z.number().nullable().default(null),
  eff_weight_sum: z.number().nullable().default(null),
}).passthrough();

// ─────────────────────── pure assembler ───────────────────────

/** Per-metric inputs the api-worker builder gathers from D1, handed to the pure assembler. */
export interface LineageMetricInput {
  readonly canonical:  OracleCanonicalRow;              // latest row for the key (value + provenance_json + policy_version)
  readonly configs:    ReadonlyArray<OracleSourceRow>;  // oracle_sources of this metric's data_type (tier/enabled join)
  readonly health:     ReadonlyArray<OracleSourceHealthRow>; // latest oracle_source_health per source for this key (consensus)
  readonly policy:     OraclePolicyRow | null;          // precedence-resolved oracle_policy row
  readonly descriptor: AnyKindDescriptor | null;        // gateway kind descriptor (resolution/truthClock)
  readonly assetClass: string;                          // the class the policy was resolved for ('crypto'|'equity'|'*'|…)
}

function classifyDataType(dt: string): 'price' | 'macro' {
  return dt === 'macro' ? 'macro' : 'price';
}

function consumersFor(dataType: 'price' | 'macro'): LineageConsumerView[] {
  return CONSUMER_MAP.filter((c) => c.data_type === dataType).map((c) => ({
    reader: c.reader, file: c.file, clock: c.clock, purpose: c.purpose,
  }));
}

// The macro series `status` column is a free string; coerce to the view enum. A present value with a
// null status (every Group-C / crypto-derived field never routed through resolveAuthoritative) reads
// as 'ok'; a genuinely absent value is 'no_candidates'.
function coerceMacroStatus(s: string | null, hasValue: boolean): 'ok' | 'below_quorum' | 'no_candidates' {
  if (s === 'ok' || s === 'below_quorum' || s === 'no_candidates') return s;
  return hasValue ? 'ok' : 'no_candidates';
}

/** Assemble ONE metric's lineage row. Pure — parses provenance_json, joins used[]/rejected[] with
 *  the source config + health verdict, attaches the policy + the static consumer set. */
export function assembleLineageMetric(input: LineageMetricInput): LineageMetricView {
  const { canonical, configs, health, policy, descriptor, assetClass } = input;
  const dataType = classifyDataType(canonical.data_type);

  // provenance (parse-don't-cast). This is a per-row READ-ONLY projection, so a single corrupt
  // provenance_json must NOT 500 the whole lineage tab — it fails SOFT to empty sources + null
  // stats. The metric still renders (value/policy/consumers intact) with a visibly empty Sources
  // table, which reads as "provenance unavailable" rather than fabricating candidates.
  let prov: z.infer<typeof ProvenanceSchema>;
  try {
    prov = ProvenanceSchema.parse(JSON.parse(canonical.provenance_json));
  } catch {
    prov = { used: [], rejected: [], median: null, spread: null, mad: null, threshold: null, eff_weight_sum: null };
  }

  const cfgById = new Map(configs.map((c) => [c.source_id, c]));
  // latest health row per source_id (health may carry >1 fire; keep the newest per source)
  const healthById = new Map<string, OracleSourceHealthRow>();
  for (const h of health) {
    const prev = healthById.get(h.source_id);
    if (!prev || h.created_at > prev.created_at) healthById.set(h.source_id, h);
  }

  const sources: LineageSourceView[] = [
    ...prov.used.map((u): LineageSourceView => {
      const cfg = cfgById.get(u.source_id);
      const h = healthById.get(u.source_id);
      return {
        source_id:   u.source_id,
        role:        'used',
        value:       u.value,
        weight:      u.weight,
        f_freshness: u.f_freshness ?? h?.f_freshness ?? null,
        verdict:     h?.verdict ?? null,
        reason:      null,
        tier:        cfg?.tier ?? null,
        enabled:     cfg ? cfg.enabled === 1 : null,
      };
    }),
    ...prov.rejected.map((r): LineageSourceView => {
      const cfg = cfgById.get(r.source_id);
      const h = healthById.get(r.source_id);
      return {
        source_id:   r.source_id,
        role:        'rejected',
        value:       r.value,
        weight:      null,
        f_freshness: h?.f_freshness ?? null,
        verdict:     h?.verdict ?? null,
        reason:      r.reason,
        tier:        cfg?.tier ?? null,
        enabled:     cfg ? cfg.enabled === 1 : null,
      };
    }),
  ];

  return {
    key:            canonical.key,
    data_type:      dataType,
    value:          canonical.value,
    value_text:     null,   // price metrics are always numeric
    status:         canonical.status,
    observed_at:    canonical.observed_at,
    created_at:     canonical.created_at,
    policy_version: canonical.policy_version,
    policy: {
      mode:        policy?.mode ?? 'consensus',
      asset_class: assetClass,
      resolution:  descriptor?.resolution ?? null,
      truth_clock: descriptor?.truthClock ?? null,
    },
    sources,
    n_corroborators: null,  // consensus provenance is the used[]/rejected[] set above, not a scalar count
    eff_weight_sum: prov.eff_weight_sum,
    median:         prov.median,
    spread:         prov.spread,
    mad:            prov.mad,
    threshold:      prov.threshold,
    consumers:      consumersFor(dataType),
    usage:          null,   // v1: AE usage reader deferred (docs/oracle-lineage-scout-2026-07-21.md §5)
  };
}

/** Per-field input for the MACRO lineage path. Macro values live in oracle_macro_series (NOT
 *  oracle_canonical, whose macro rows are empty no_candidates shells) — verified against live D1
 *  2026-07-21. Macro is AUTHORITATIVE + assembled: a single issuer per field, no consensus vote, so
 *  there is no used/rejected set or MAD math. The "source" is the issuer (FRED for FRED-derived
 *  fields; NULL → an assembled Group-C/crypto field whose sub-vendor the series does not record). */
export interface LineageMacroFieldInput {
  readonly row:        OracleMacroSeriesRow;
  readonly descriptor: AnyKindDescriptor | null;   // MACRO_DESCRIPTOR (resolution='assembled', clock)
  readonly policyVersion: string;                   // macro oracle_policy policy_version (or a default)
}

/** Assemble ONE macro field's lineage row from its oracle_macro_series row. Pure. */
export function assembleLineageMacroField(input: LineageMacroFieldInput): LineageMetricView {
  const { row, descriptor, policyVersion } = input;
  const hasValue = row.value_num !== null || row.value_text !== null;
  // The single authoritative source = the issuer. FRED fields carry issuer='fred'; a Group-C/crypto
  // field has issuer=null — its value is assembled from a vendor the per-field row does not record,
  // surfaced honestly as 'assembled' rather than a fabricated source name.
  const sources: LineageSourceView[] = [{
    source_id:   row.issuer ?? 'assembled',
    role:        hasValue ? 'used' : 'rejected',
    value:       row.value_num,
    weight:      null,
    f_freshness: null,
    verdict:     null,
    reason:      hasValue ? null : 'no_value',
    tier:        null,
    enabled:     null,
  }];

  return {
    key:            row.field,
    data_type:      'macro',
    value:          row.value_num,
    value_text:     row.value_text,
    status:         coerceMacroStatus(row.status, hasValue),
    observed_at:    row.observed_at,
    created_at:     row.created_at,
    policy_version: policyVersion,
    policy: {
      mode:        'authoritative',
      asset_class: '*',
      resolution:  descriptor?.resolution ?? null,   // 'assembled'
      truth_clock: descriptor?.truthClock ?? null,    // 'created'
    },
    sources,
    n_corroborators: row.n_corroborators,
    // no consensus vote for an authoritative single-issuer field
    eff_weight_sum: null,
    median:         null,
    spread:         null,
    mad:            null,
    threshold:      null,
    consumers:      consumersFor('macro'),
    usage:          null,
  };
}

/** Wrap already-assembled metric views into the bundle (sorted by key). Pure. The builder assembles
 *  price via assembleLineageMetric and macro via assembleLineageMacroField, then hands the union here
 *  — the two data_types read from different stores (oracle_canonical vs oracle_macro_series) so they
 *  cannot share one input shape. */
export function assembleLineageBundle(input: {
  readonly generatedAt: string;
  readonly metrics:     ReadonlyArray<LineageMetricView>;
}): LineageBundle {
  return {
    generated_at: input.generatedAt,
    metrics: [...input.metrics].sort((a, b) => a.key.localeCompare(b.key)),
  };
}
